import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import type {
  Service,
  ProposalService,
  PaginatedServiceResponse,
} from "../types/proposal.types";
import apiService from "../../../service/Api";
import { SafeHtml } from "../proposal-preview/components/safeHtml";

interface AddServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (proposalService: ProposalService) => void;
}

type BackendService = PaginatedServiceResponse["results"][number];

const AddServiceDialog: React.FC<AddServiceDialogProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [customizations, setCustomizations] = useState("");
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [prevPageUrl, setPrevPageUrl] = useState<string | null>(null);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);

  const fetchServices = useCallback(
    async (url?: string) => {
      setLoading(true);
      try {
        let endpoint = url || "/api/packages/package-service/";

        if (!url) {
          if (selectedCategory !== "all") {
            endpoint += `?service_category=${encodeURIComponent(
              selectedCategory,
            )}`;
          }
        }

        const response =
          await apiService.get<PaginatedServiceResponse>(endpoint);
        const backendServices = response.data.results;

        const transformed: Service[] = backendServices.map(
          (svc: BackendService) => ({
            ...svc,
            price: Number(svc.price),
            customizable: false,
            features: [],
          }),
        );

        setServices(transformed);
        setNextPageUrl(response.data.next);
        setPrevPageUrl(response.data.previous);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      } finally {
        setLoading(false);
      }
    },
    [selectedCategory],
  );

  /** 🔹 Refetch services when dialog opens or category changes */
  useEffect(() => {
    if (open) fetchServices();
  }, [open, selectedCategory, fetchServices]);

  /** 🔹 Handle pagination */
  const handleNextPage = () => {
    if (nextPageUrl) fetchServices(nextPageUrl);
  };

  const handlePrevPage = () => {
    if (prevPageUrl) fetchServices(prevPageUrl);
  };

  /** 🔹 Extract unique categories for sidebar */
  const uniqueCategories = [
    { id: "all", label: "All" },
    ...Array.from(
      new Map(
        services.map((s) => [
          s.service_category,
          {
            id: s.service_category,
            label: s.service_category,
          },
        ]),
      ).values(),
    ),
  ];

  /** 🔹 Add selected service to proposal */
  const handleAddService = () => {
    if (!selectedService) return;

    const proposalService: ProposalService = {
      service: selectedService,
      quantity,
      customizations: customizations || undefined,
    };

    onAdd(proposalService);
    setSelectedService(null);
    setQuantity(1);
    setCustomizations("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Add Service</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 3, minHeight: 500 }}>
          {/* Sidebar Categories */}
          <Box
            sx={{
              flex: "0 0 200px",
              borderRight: 1,
              borderColor: "divider",
              pr: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Categories
            </Typography>
            <List>
              {uniqueCategories.map((category) => (
                <ListItem key={category.id} disablePadding>
                  <ListItemButton
                    selected={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <Typography variant="body2">{category.label}</Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            {loading ? (
              <Typography>Loading services...</Typography>
            ) : !selectedService ? (
              <>
                {/* Services List */}
                {services.map((service) => (
                  <Box
                    key={service.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => setSelectedService(service)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {service.name}
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary">
                          Base price
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          ${service.price.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <SafeHtml
                        html={service.description ?? "No Description available"}
                      />
                    </Typography>
                  </Box>
                ))}

                {/* Pagination Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    disabled={!prevPageUrl}
                    onClick={handlePrevPage}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!nextPageUrl}
                    onClick={handleNextPage}
                  >
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              // Service Details View
              <Box>
                <Button onClick={() => setSelectedService(null)} sx={{ mb: 2 }}>
                  ← Back to services
                </Button>

                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                  {selectedService.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  <SafeHtml
                    html={
                      selectedService.description ?? "No Description available"
                    }
                  />
                </Typography>

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(
                      Math.max(1, Number.parseInt(e.target.value) || 1),
                    )
                  }
                  sx={{ mb: 2 }}
                  inputProps={{ min: 1 }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: colors.primary[800],
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    ${(selectedService.price * quantity).toLocaleString()}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddService}
                  sx={{ mt: 2, bgcolor: colors.primary[400] }}
                >
                  Add to Proposal
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddServiceDialog;
